import type { ChannelShareData } from "@/call-details/api/types";
import { useProductOfferStore } from "@/sales-tools";
import type { Partner, PartnerProgram } from "@/shared-types";
import { getHashedValue, removeCountryCode, validatePhoneNumber } from "@/shared-utils";

export const MAX_MDN_REASON = "Maximum number of MDNs checked for eligibility";

export interface CalcIsConfirmationDisabledParams {
    customerName: string | undefined;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    mdn: string;
    verificationFormIsValid: boolean;
    additionalDetailsFormIsValid: boolean;
    isEditingVerificationInput: boolean;
    showProgramSelection: boolean | undefined;
    customerProgram: PartnerProgram | undefined;
    mdnLookupResult: {
        hasReachedMaxMdnLookups: boolean;
        previousHashedMdnLookups: string[];
    };
}

export interface CalcIsConfirmationDisabledResult {
    isConfirmationDisabled: boolean;
    reason?: string;
}

export const calcIsConfirmationDisabled = async ({
    customerName,
    verificationType,
    mdn,
    verificationFormIsValid,
    additionalDetailsFormIsValid,
    isEditingVerificationInput,
    showProgramSelection,
    customerProgram,
    mdnLookupResult,
}: CalcIsConfirmationDisabledParams): Promise<CalcIsConfirmationDisabledResult> => {
    // Check if we've reached the MDN lookup limit based on API response
    if (mdnLookupResult.hasReachedMaxMdnLookups && mdn?.trim() !== "") {
        const strippedMdn = removeCountryCode(mdn);
        const currentHashedMdn = await getHashedValue(strippedMdn, "SHA-256");
        const isCurrentMdnAlreadyLookedUp = mdnLookupResult.previousHashedMdnLookups.includes(currentHashedMdn);

        // If current MDN is not in the list and we've reached the limit, disable confirmation
        if (!isCurrentMdnAlreadyLookedUp) {
            return {
                isConfirmationDisabled: true,
                reason: MAX_MDN_REASON,
            };
        }
    }

    const isDisabled = Boolean(
        !customerName ||
            (verificationType === "phoneNumber" && !validatePhoneNumber(mdn).valid) ||
            !verificationFormIsValid ||
            !additionalDetailsFormIsValid ||
            isEditingVerificationInput ||
            (showProgramSelection && !customerProgram),
    );

    return {
        isConfirmationDisabled: isDisabled,
    };
};

export const useMdnLookupResult = (sessionId: string) => {
    const eligibilityMeta = useProductOfferStore((state) => state.getEligibilityMetaBySessionId(sessionId));

    try {
        return eligibilityMeta.mdnLookupResult;
    } catch (e) {
        console.error(e);
        return {
            hasReachedMaxMdnLookups: false,
            previousHashedMdnLookups: [],
        };
    }
};

/**
 * GAIA update-session-context requires that we do not send null or undefined values on the properties list.
 */
export function sanitizeGaiaSessionContextProperties(properties: Array<Record<string, unknown>>) {
    return properties.filter((prop) => prop.value !== null && prop.value !== undefined);
}

export function shouldShowCallIntroFlowQuestions(partner: Partner, callDetails: ChannelShareData): boolean {
    return partner !== "youtubetv" || (partner === "youtubetv" && callDetails?.CLIENT !== "ASURION_CARE_STREAMING");
}

/**
 * Converts a mask pattern to a regular expression for validation.
 *
 * Mask characters:
 * - 'a' represents any letter (a-z, A-Z)
 * - '0' represents any digit (0-9)
 * - All other characters are treated as literal characters
 *
 * @param mask - The mask pattern (e.g., "000-0000000-0000000", "aa-00000000")
 * @returns RegExp - A regular expression that matches the mask pattern
 *
 * @example
 * maskToRegex("000-0000000-0000000") // Returns /^\d\d\d-\d\d\d\d\d\d\d-\d\d\d\d\d\d\d$/
 * maskToRegex("aa-00000000") // Returns /^[a-zA-Z][a-zA-Z]-\d\d\d\d\d\d\d\d$/
 */
export function maskToRegex(mask: string): RegExp {
    const escapedMask = mask.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regexPattern = escapedMask
        .replace(/a/g, "[a-zA-Z]") // 'a' becomes letter character class
        .replace(/0/g, "\\d"); // '0' becomes digit character class
    return new RegExp(`^${regexPattern}$`);
}
